/**
 * Precast fabrication services (BIM Precast > Fabrication panel).
 *
 * Derives shop-drawing sheets and machine (CAM) data from the precast model:
 *  - buildShopDrawingSvg(): one A-series landscape sheet per panel — elevation
 *    (wall) or plan (floor) outline, overall dimensions, joint gaps, embedded
 *    mounting parts and the reinforcement grid, plus a small bar takeoff.
 *  - buildCamPayload(): exports the selected panels to industry formats
 *    (PXML-style XML, Unitechnik-style XML, bar-bending CSV, cut-list CSV).
 *
 * All SVG coordinates are in real-world millimeters scaled onto the sheet;
 * the drawing itself is pure string generation — no canvas dependency.
 */
import {
    BIMElement,
    PrecastWallElement,
    PrecastFloorElement,
    PrecastMountingPartElement,
    PrecastReinforcementElement,
} from '../core/types';
import { PrecastConfiguration, PRECAST_SHEET_SIZES } from '../state/usePrecastConfigStore';
import {
    polygonBounds,
    reinforcementTotalLength,
    reinforcementWeightKg,
    wallPanelWeightMeters,
    floorPanelWeightMeters,
} from './precastGeometry';

const M_TO_MM = 1000;

/** True for sheet-able panel members (wall/floor); columns/beams are not
 *  drawn on shop sheets by this service and are excluded from the predicate. */
export function isPrecastPanel(element: BIMElement): element is PrecastWallElement | PrecastFloorElement {
    return element.category === 'Precast Walls' || element.category === 'Precast Floors';
}

/** Panel envelope size in millimeters for the shop sheet. */
export function panelSizeMm(panel: PrecastWallElement | PrecastFloorElement): { width: number; height: number; thickness: number } {
    if (panel.category === 'Precast Walls') {
        const g = panel.geometryData;
        const length = Math.hypot(g.end[0] - g.start[0], g.end[1] - g.start[1]) * M_TO_MM;
        return { width: length, height: g.height * M_TO_MM, thickness: g.thickness * M_TO_MM };
    }
    const g = panel.geometryData;
    const b = polygonBounds(g.boundary);
    const dx = b.maxX - b.minX, dy = b.maxY - b.minY;
    const cu = Math.abs(Math.cos(g.spanAngle)), su = Math.abs(Math.sin(g.spanAngle));
    // Rotated-frame extents of the axis-aligned footprint bbox.
    const spanExtent = (dx * cu + dy * su) * M_TO_MM;     // along the span
    const transExtent = (dx * su + dy * cu) * M_TO_MM;    // plank width
    return { width: spanExtent, height: transExtent, thickness: g.thickness * M_TO_MM };
}

function svgNumber(v: number): string {
    return Number.isFinite(v) ? v.toFixed(1) : '0';
}

function dimensionLineH(x1: number, x2: number, y: number, label: string, fs: number): string {
    const tick = fs * 0.5;
    return `<g stroke="#000" stroke-width="${svgNumber(fs * 0.06)}" fill="none">
<path d="M ${svgNumber(x1)} ${svgNumber(y - tick)} L ${svgNumber(x1)} ${svgNumber(y + tick)}"/>
<path d="M ${svgNumber(x2)} ${svgNumber(y - tick)} L ${svgNumber(x2)} ${svgNumber(y + tick)}"/>
<path d="M ${svgNumber(x1)} ${svgNumber(y)} L ${svgNumber(x2)} ${svgNumber(y)}"/>
</g><text x="${svgNumber((x1 + x2) / 2)}" y="${svgNumber(y - fs * 0.6)}" font-size="${svgNumber(fs)}" text-anchor="middle" fill="#000">${label}</text>`;
}

function dimensionLineV(y1: number, y2: number, x: number, label: string, fs: number): string {
    const tick = fs * 0.5;
    return `<g stroke="#000" stroke-width="${svgNumber(fs * 0.06)}" fill="none">
<path d="M ${svgNumber(x - tick)} ${svgNumber(y1)} L ${svgNumber(x + tick)} ${svgNumber(y1)}"/>
<path d="M ${svgNumber(x - tick)} ${svgNumber(y2)} L ${svgNumber(x + tick)} ${svgNumber(y2)}"/>
<path d="M ${svgNumber(x)} ${svgNumber(y1)} L ${svgNumber(x)} ${svgNumber(y2)}"/>
</g><text x="${svgNumber(x - fs * 0.6)}" y="${svgNumber((y1 + y2) / 2)}" font-size="${svgNumber(fs)}" text-anchor="middle" fill="#000" transform="rotate(-90 ${svgNumber(x - fs * 0.6)} ${svgNumber((y1 + y2) / 2)})">${label}</text>`;
}

// ==================== Shop drawing sheet ====================

export interface ShopDrawingOptions {
    config: PrecastConfiguration;
    projectName: string;
    sheetNumber: string;
}

/**
 * Build a complete shop-drawing SVG sheet for one precast panel. Mounting
 * parts and reinforcement elements hosted by the panel are projected onto
 * the same elevation/plan face per the Configuration display flags.
 */
export function buildShopDrawingSvg(
    panel: PrecastWallElement | PrecastFloorElement,
    mountingParts: PrecastMountingPartElement[],
    reinforcements: PrecastReinforcementElement[],
    options: ShopDrawingOptions,
): string {
    const { config, projectName, sheetNumber } = options;
    const sheet = PRECAST_SHEET_SIZES.find(s => s.id === config.shopSheetSizeId) ?? PRECAST_SHEET_SIZES[2];
    const W = sheet.widthMm, H = sheet.heightMm;
    const margin = Math.min(W, H) * 0.08;
    const fs = Math.min(W, H) / 32; // title text size
    const fsDim = fs * 0.75;

    const size = panelSizeMm(panel);
    const isWall = panel.category === 'Precast Walls';

    // Fit the panel envelope into the drawing field (below title block band).
    const fieldW = W - margin * 2;
    const fieldH = H - margin * 2 - fs * 4;
    const scale = Math.min(fieldW / (size.width + size.height * 0.4), fieldH / (size.height + size.width * 0.15));
    const dw = size.width * scale, dh = size.height * scale;
    const ox = margin + (fieldW - dw) / 2;
    const oy = margin + fs * 2.5 + (fieldH - dh) / 2;

    let body = '';

    // ---- Panel outline (elevation for walls, plan for floors) ----
    let uMin = 0, vMin = 0;
    if (isWall) {
        body += `<rect x="${svgNumber(ox)}" y="${svgNumber(oy)}" width="${svgNumber(dw)}" height="${svgNumber(dh)}" fill="none" stroke="#000" stroke-width="${svgNumber(0.5 * scale * M_TO_MM / 100)}"/>`;
        // Joint gaps at both vertical edges.
        const gap = panel.geometryData.jointWidth * M_TO_MM * scale;
        if (gap > 0.5) {
            body += `<rect x="${svgNumber(ox - gap)}" y="${svgNumber(oy)}" width="${svgNumber(gap)}" height="${svgNumber(dh)}" fill="#e0e0e0" stroke="#888" stroke-width="${svgNumber(fs * 0.05)}"/>`;
            body += `<rect x="${svgNumber(ox + dw)}" y="${svgNumber(oy)}" width="${svgNumber(gap)}" height="${svgNumber(dh)}" fill="#e0e0e0" stroke="#888" stroke-width="${svgNumber(fs * 0.05)}"/>`;
        }
    } else {
        const g = panel.geometryData;
        const cu = Math.cos(g.spanAngle), su = Math.sin(g.spanAngle);
        const uVals = g.boundary.map(([x, y]) => x * cu + y * su);
        const vVals = g.boundary.map(([x, y]) => x * -su + y * cu);
        uMin = Math.min(...uVals);
        vMin = Math.min(...vVals);
        const pts = g.boundary.map(([x, y]) => {
            const u = x * cu + y * su, v = x * -su + y * cu;
            return [ox + (u - uMin) * scale, oy + (v - vMin) * scale] as [number, number];
        });
        const poly = pts.map(p => `${svgNumber(p[0])},${svgNumber(p[1])}`).join(' ');
        body += `<polygon points="${poly}" fill="none" stroke="#000" stroke-width="${svgNumber(0.5 * scale * M_TO_MM / 100)}"/>`;
        // Hollow-core void sections, drawn as dashed circles across the plank.
        if (g.hollowCore && g.core) {
            const u0 = uMin + g.core.spacing / 2;
            for (let i = 0; i < g.core.count; i++) {
                const u = u0 + i * g.core.spacing;
                const cx = ox + (u - uMin) * scale;
                body += `<circle cx="${svgNumber(cx)}" cy="${svgNumber(oy + dh / 2)}" r="${svgNumber(g.core.diameter * M_TO_MM * scale / 2)}" fill="none" stroke="#666" stroke-width="${svgNumber(fs * 0.05)}" stroke-dasharray="${svgNumber(fs * 0.3)} ${svgNumber(fs * 0.15)}"/>`;
            }
        }
    }

    // ---- Overall dimensions ----
    body += dimensionLineH(ox, ox + dw, oy + dh + fs * 1.6, `${Math.round(size.width)}`, fsDim);
    body += dimensionLineV(oy, oy + dh, ox - fs * 1.8, `${Math.round(size.height)}`, fsDim);
    body += dimensionLineH(ox, ox + dw, oy - fs * 0.8, `${Math.round(size.width)}`, fsDim);

    // ---- Reinforcement layer ----
    if (config.showReinforcement) {
        for (const reinf of reinforcements) {
            if (reinf.geometryData.hostId !== panel.id) continue;
            body += reinforcementSvg(reinf, panel, scale, ox, oy, uMin, vMin, fs);
        }
    }

    // ---- Mounting parts layer ----
    if (config.showMountingParts) {
        const parts = mountingParts.filter(p => p.geometryData.hostId === panel.id);
        for (const part of parts) {
            const g = part.geometryData;
            let px = ox + dw / 2, py = oy + dh / 2;
            if (isWall) {
                const pg = panel.geometryData;
                const length = Math.hypot(pg.end[0] - pg.start[0], pg.end[1] - pg.start[1]);
                if (length > 0) {
                    const ux = (pg.end[0] - pg.start[0]) / length, uy = (pg.end[1] - pg.start[1]) / length;
                    const dAlong = (g.position[0] - pg.start[0]) * ux + (g.position[1] - pg.start[1]) * uy;
                    px = ox + dAlong * M_TO_MM * scale;
                    py = oy + dh - (g.position[2] - pg.baseOffset) * M_TO_MM * scale;
                }
            }
            const r = Math.max(g.size * M_TO_MM * scale / 2, fs * 0.25);
            body += `<circle cx="${svgNumber(px)}" cy="${svgNumber(py)}" r="${svgNumber(r)}" fill="none" stroke="#c8641e" stroke-width="${svgNumber(fs * 0.08)}"/>`;
            body += `<text x="${svgNumber(px)}" y="${svgNumber(py - r - fs * 0.3)}" font-size="${svgNumber(fsDim * 0.8)}" text-anchor="middle" fill="#c8641e">${part.geometryData.kind}</text>`;
        }
    }

    // ---- Takeoff table (right gutter) ----
    const weight = isWall
        ? wallPanelWeightMeters(panel as PrecastWallElement, config.concreteDensityKgM3)
        : floorPanelWeightMeters(panel as PrecastFloorElement, config.concreteDensityKgM3);
    const reinfLen = reinforcements
        .filter(r => r.geometryData.hostId === panel.id)
        .reduce((sum, r) => sum + reinforcementTotalLength(r), 0);
    const reinfKg = reinforcements
        .filter(r => r.geometryData.hostId === panel.id)
        .reduce((sum, r) => sum + reinforcementWeightKg(r), 0);
    let table = '';
    if (config.showSchedule) {
        const rows = [
            ['Panel Mark', panel.geometryData.mark],
            ['Type', panel.type],
            ['Thickness', `${Math.round(size.thickness)} mm`],
            ['Concrete', `${(weight / 1000).toFixed(2)} t`],
            ['Steel', reinfLen > 0 ? `${reinfKg.toFixed(1)} kg` : '—'],
            ['Scale', `1:${config.shopDrawingScale}`],
        ];
        const tx = W - margin - fs * 9, ty = margin + fs * 1.2;
        table = rows.map(([k, v], i) =>
            `<text x="${svgNumber(tx)}" y="${svgNumber(ty + i * fs * 1.15)}" font-size="${svgNumber(fs * 0.7)}" fill="#000">${k}: ${v}</text>`
        ).join('')
            + `<rect x="${svgNumber(tx - fs * 0.4)}" y="${svgNumber(ty - fs * 0.9)}" width="${svgNumber(fs * 9.6)}" height="${svgNumber(rows.length * fs * 1.15 + fs * 0.3)}" fill="none" stroke="#000" stroke-width="${svgNumber(fs * 0.05)}"/>`;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}" font-family="Arial, sans-serif">
<rect width="${W}" height="${H}" fill="#ffffff"/>
<rect x="${margin * 0.5}" y="${margin * 0.5}" width="${W - margin}" height="${H - margin}" fill="none" stroke="#000" stroke-width="${svgNumber(fs * 0.08)}"/>
<text x="${svgNumber(margin)}" y="${svgNumber(margin + fs)}" font-size="${svgNumber(fs * 1.2)}" font-weight="bold" fill="#000">${projectName} — Precast Shop Drawing</text>
<text x="${svgNumber(margin)}" y="${svgNumber(margin + fs * 2.1)}" font-size="${svgNumber(fs * 0.8)}" fill="#000">Sheet ${sheetNumber} · Panel ${panel.geometryData.mark} · ${isWall ? 'Elevation' : 'Plan'}</text>
${body}
${table}
<text x="${svgNumber(W / 2)}" y="${svgNumber(H - margin * 0.5 + fs * 0.8)}" font-size="${svgNumber(fs * 0.6)}" text-anchor="middle" fill="#555">Generated by Yap Precast · 1:${config.shopDrawingScale} · dimensions in mm</text>
</svg>`;
}

// Project a reinforcement element's bar segments into the panel's drawing
// frame: walls show the elevation grid (u along the run, z up), floors show
// the plan grid in the span frame (u, v). Bars inside one cover layer of a
// two-face mesh are drawn twice — once solid, once dashed — to read like a
// BIM precast reinforcement drawing.
function reinforcementSvg(
    reinf: PrecastReinforcementElement,
    panel: PrecastWallElement | PrecastFloorElement,
    scale: number,
    ox: number,
    oy: number,
    uMin: number,
    vMin: number,
    fs: number,
): string {
    const stroke = fs * 0.04;
    let out = '';
    const isWall = panel.category === 'Precast Walls';
    if (isWall) {
        const pg = panel.geometryData;
        const length = Math.hypot(pg.end[0] - pg.start[0], pg.end[1] - pg.start[1]);
        if (length <= 0) return '';
        const ux = (pg.end[0] - pg.start[0]) / length, uy = (pg.end[1] - pg.start[1]) / length;
        let dash = false; // second face layer of a two-face mesh
        const faceSign = new Set<number>();
        for (const b of reinf.geometryData.bars) {
            const sU = (b.start[0] - pg.start[0]) * ux + (b.start[1] - pg.start[1]) * uy;
            const sOff = -(b.start[0] - pg.start[0]) * uy + (b.start[1] - pg.start[1]) * ux;
            const eU = (b.end[0] - pg.start[0]) * ux + (b.end[1] - pg.start[1]) * uy;
            const sZ = b.start[2] - pg.baseOffset, eZ = b.end[2] - pg.baseOffset;
            if (faceSign.size === 0) faceSign.add(Math.sign(sOff));
            else if (!faceSign.has(Math.sign(sOff))) { faceSign.add(Math.sign(sOff)); dash = true; }
            const x1 = ox + sU * M_TO_MM * scale, y1 = oy + (pg.height - sZ) * M_TO_MM * scale;
            const x2 = ox + eU * M_TO_MM * scale, y2 = oy + (pg.height - eZ) * M_TO_MM * scale;
            out += `<line x1="${svgNumber(x1)}" y1="${svgNumber(y1)}" x2="${svgNumber(x2)}" y2="${svgNumber(y2)}" stroke="#00606c" stroke-width="${svgNumber(stroke)}"${dash ? ` stroke-dasharray="${svgNumber(fs * 0.4)} ${svgNumber(fs * 0.2)}"` : ''}/>`;
        }
    } else {
        const g = panel.geometryData;
        const cu = Math.cos(g.spanAngle), su = Math.sin(g.spanAngle);
        for (const b of reinf.geometryData.bars) {
            const sU = b.start[0] * cu + b.start[1] * su, sV = b.start[0] * -su + b.start[1] * cu;
            const eU = b.end[0] * cu + b.end[1] * su, eV = b.end[0] * -su + b.end[1] * cu;
            out += `<line x1="${svgNumber(ox + (sU - uMin) * M_TO_MM * scale)}" y1="${svgNumber(oy + (sV - vMin) * M_TO_MM * scale)}" x2="${svgNumber(ox + (eU - uMin) * M_TO_MM * scale)}" y2="${svgNumber(oy + (eV - vMin) * M_TO_MM * scale)}" stroke="#00606c" stroke-width="${svgNumber(stroke)}" opacity="0.75"/>`;
        }
    }
    return out;
}

// ==================== CAM export ====================

export interface CamPayload {
    fileName: string;
    content: string;
    mime: string;
}

function esc(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Build the CAM file payload for the selected panels in the requested format.
 * Production machinery consumes panel geometry + reinforcement + embeds; the
 * CSV flavors are the pragmatic bridge for bar-bending and cut-list stations.
 */
export function buildCamPayload(
    panels: Array<PrecastWallElement | PrecastFloorElement>,
    reinforcements: PrecastReinforcementElement[],
    mountingParts: PrecastMountingPartElement[],
    config: PrecastConfiguration,
): CamPayload {
    const stamp = new Date().toISOString().slice(0, 10);
    const reinfFor = (id: string) => reinforcements.filter(r => r.geometryData.hostId === id);

    switch (config.camFormat) {
        case 'Bar Bending CSV': {
            const lines = ['PanelMark,BarNo,Role,Diameter_mm,Length_mm,Count'];
            for (const p of panels) {
                reinfFor(p.id).forEach((r) => {
                    const grouped = new Map<string, number>();
                    r.geometryData.bars.forEach(b => {
                        const len = Math.round(Math.hypot(
                            (b.end[0] - b.start[0]) * M_TO_MM,
                            (b.end[1] - b.start[1]) * M_TO_MM,
                            (b.end[2] - b.start[2]) * M_TO_MM,
                        ));
                        const key = `${b.role}|${len}`;
                        grouped.set(key, (grouped.get(key) ?? 0) + 1);
                    });
                    let barNo = 0;
                    grouped.forEach((count, key) => {
                        const [role, len] = key.split('|');
                        barNo += 1;
                        lines.push(`${p.geometryData.mark},${barNo},${role},${r.geometryData.wireDiameter},${len},${count}`);
                    });
                });
            }
            return { fileName: `precast-bar-bending-${stamp}.csv`, content: lines.join('\n'), mime: 'text/csv' };
        }
        case 'Rebar Cut List (CSV)': {
            const lines = ['PanelMark,Part,Quantity'];
            for (const p of panels) {
                const size = panelSizeMm(p);
                lines.push(`${p.geometryData.mark},Concrete_${Math.round(size.width)}x${Math.round(size.height)}x${Math.round(size.thickness)}mm,1`);
                const steelKg = reinfFor(p.id).reduce((s, r) => s + reinforcementWeightKg(r), 0);
                lines.push(`${p.geometryData.mark},SteelMesh_W${reinfFor(p.id)[0]?.geometryData.wireDiameter ?? 0}_spacing_${reinfFor(p.id)[0]?.geometryData.spacing ?? 0}mm,${steelKg.toFixed(1)}kg`);
                const embeds = mountingParts.filter(m => m.geometryData.hostId === p.id);
                embeds.forEach(m => lines.push(`${p.geometryData.mark},Embed_${m.geometryData.kind.replace(/\s+/g, '_')},1`));
            }
            return { fileName: `precast-cut-list-${stamp}.csv`, content: lines.join('\n'), mime: 'text/csv' };
        }
        case 'Unitechnik (XML)':
        case 'PXML':
        default: {
            const isUni = config.camFormat === 'Unitechnik (XML)';
            const root = isUni ? 'UNITECHNIK_EXPORT' : 'PXML';
            let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${root} version="2.0" factory="${esc(config.factoryName)}" date="${stamp}">\n`;
            for (const p of panels) {
                const size = panelSizeMm(p);
                const weight = p.category === 'Precast Walls'
                    ? wallPanelWeightMeters(p as PrecastWallElement, config.concreteDensityKgM3)
                    : floorPanelWeightMeters(p as PrecastFloorElement, config.concreteDensityKgM3);
                xml += `  <Panel id="${esc(p.id)}" mark="${esc(p.geometryData.mark)}" kind="${esc(p.category)}" width_mm="${svgNumber(size.width)}" height_mm="${svgNumber(size.height)}" thickness_mm="${svgNumber(size.thickness)}" weight_kg="${weight.toFixed(1)}">\n`;
                for (const r of reinfFor(p.id)) {
                    xml += `    <Reinforcement style="${esc(r.type)}" diameter_mm="${r.geometryData.wireDiameter}" spacing_mm="${r.geometryData.spacing}" weight_kg="${reinforcementWeightKg(r).toFixed(1)}">\n`;
                    r.geometryData.bars.forEach((b, i) => {
                        const len = Math.hypot(
                            (b.end[0] - b.start[0]) * M_TO_MM,
                            (b.end[1] - b.start[1]) * M_TO_MM,
                            (b.end[2] - b.start[2]) * M_TO_MM,
                        );
                        xml += `      <Bar no="${i + 1}" role="${b.role}" length_mm="${len.toFixed(1)}"/>\n`;
                    });
                    xml += `    </Reinforcement>\n`;
                }
                for (const m of mountingParts.filter(mm => mm.geometryData.hostId === p.id)) {
                    xml += `    <MountingPart kind="${esc(m.geometryData.kind)}" size_mm="${m.geometryData.size}" position_mm="${m.geometryData.position.map(v => (v * M_TO_MM).toFixed(1)).join(';')}"/>\n`;
                }
                xml += `  </Panel>\n`;
            }
            xml += `</${root}>\n`;
            return {
                fileName: `precast-${isUni ? 'unitechnik' : 'pxml'}-${stamp}.${isUni ? 'xml' : 'xml'}`,
                content: xml,
                mime: 'application/xml',
            };
        }
    }
}
